import { makeStyles, Theme, createStyles } from "@material-ui/core";
import * as React from "react";
import SimpleTable from "../../../components/SimpleTable";
import { useHubEventDateWindow } from "../../../core/hooks/useHubHooks";
import InstallationModel from "../../../core/models/Installation";
import {
  EVENT_EXPORT_EXCEL_CONSUMPTION_HISTORY,
  EVENT_EXPORT_PDF_CONSUMPTION_HISTORY,
  EVENT_INSTALLATION_CONSUMPTION_HISTORY_FILTER_DATE_CHANGE,
  EVENT_INSTALLATION_CONSUMPTION_HISTORY_NORMALIZATION_CONFIG_CHANGE,
  EVENT_INSTALLATION_CONSUMPTION_HISTORY_POINT_CONFIG_CHANGE,
  EVENT_REFRESH_INSTALLATION_DETAIL_PAGE,
} from "../../../Events";
import {
  FETCH_EXCEL_FILE_CONSUMPTION_HISTORY,
  FETCH_INSTALLATION_CONSUMPTION,
  FETCH_PDF_FILE_FROM_HTML,
} from "../../../Fedx";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { ConsumptionReadings } from "../../../core/models/ConsuptionReadings";
import { InstallationConsumptionServiceFetchParams } from "../../../services/InstallationConsumption";
import { formatDateTime, keepDateFromTimezonePtBr } from "../../../helpers/datetime";
import { HOURS_BETWEEN_SLOTS, HOURS_IN_THE_DAY } from "../../../core/constants/businnesConstants";
import { formatValue } from "../../../helpers/utility";
import { aggregateConsumptionReadingsDataByDay } from "../../../helpers/consumptionStatisticalHelpers";
import { IConsumptionReading, IConsumptionResult } from "../../../core/contracts/IConsumption";
import orderBy from "lodash/orderBy";
import { IExportExcel } from "../../../core/contracts/IExportExcel";
import IHtml from "../../../core/contracts/IHtml";
import ReportGrid from "../../../components/Report/ReportGrid";
import { renderToString } from "react-dom/server";
import ReportSimpleTable from "../../../components/Report/ReportSimpleTable";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      // WARNING
      // 100vh - header - tabs - toolbar
      maxHeight: `calc(100vh - ${theme.spacing(3)}px - 67px - 77px - 16px - ${
        theme.mixins.toolbar.minHeight
      }px)`,
    },
  })
);

type InstallationConsumptionHistoryProps = {
  installation?: InstallationModel;
};

type ReportDataType = {
  date: string;
  consumption: number;
  consumption_average: number;
};

function InstallationConsumptionHistory({ installation }: InstallationConsumptionHistoryProps) {
  const [loading, setLoading] = React.useState(false);
  const [
    installationConsumptionData,
    setInstallationConsumptionData,
  ] = React.useState<ConsumptionReadings | null>(null);
  const [dailyConsumptionData, setDailyConsumptionData] = React.useState<
    IConsumptionReading[] | null
  >(null);
  const [useNormalizedData, setUseNormalizedData] = React.useState(true);
  const [useDataInSlot, setUseDataInSlot] = React.useState(true);
  const [exportExcel, setExportExcel] = React.useState(false);
  const [reportData, setReportData] = React.useState<ReportDataType[]>([]);
  const [exportPdf, setExportPdf] = React.useState(false);

  const filterInterval = useHubEventDateWindow(
    EVENT_INSTALLATION_CONSUMPTION_HISTORY_FILTER_DATE_CHANGE,
    true
  );

  const classes = useStyles();
  const hub = useHubx();
  useHubXSubscribe(EVENT_EXPORT_EXCEL_CONSUMPTION_HISTORY, setExportExcel);
  useHubXSubscribe(EVENT_EXPORT_PDF_CONSUMPTION_HISTORY, setExportPdf);

  useHubXSubscribe(
    EVENT_INSTALLATION_CONSUMPTION_HISTORY_NORMALIZATION_CONFIG_CHANGE,
    setUseNormalizedData
  );
  useHubXSubscribe(EVENT_INSTALLATION_CONSUMPTION_HISTORY_POINT_CONFIG_CHANGE, setUseDataInSlot);
  useHubXSubscribe(EVENT_REFRESH_INSTALLATION_DETAIL_PAGE, fetchData);

  async function fetchFile(exportFile: IExportExcel) {
    await hub.request(FETCH_EXCEL_FILE_CONSUMPTION_HISTORY, exportFile);
  }

  async function fetchPdf(htmlFile: IHtml) {
    await hub.request(FETCH_PDF_FILE_FROM_HTML, htmlFile);
  }

  async function mapReportData(date: string, consumption: number, consumption_average: number) {
    const newReportData: ReportDataType = {
      date: date,
      consumption: consumption,
      consumption_average: consumption_average,
    };

    setReportData((reportData) => [...reportData, newReportData]);
  }

  async function fetchData() {
    if (installation?.installation_id) {
      setLoading(true);

      try {
        const fetchParams: InstallationConsumptionServiceFetchParams = {
          installation_id: installation?.installation_id,
          startDate: filterInterval?.start || undefined,
          endDate: filterInterval?.end || undefined,
        };

        if (fetchParams.startDate && fetchParams.endDate) {
          const APIInstallationResponse: ConsumptionReadings = await hub.request(
            FETCH_INSTALLATION_CONSUMPTION,
            fetchParams
          );

          const prevResult = APIInstallationResponse.readings as IConsumptionReading[];
          const resultAggregation: IConsumptionResult = aggregateConsumptionReadingsDataByDay(
            prevResult
          );
          setDailyConsumptionData(resultAggregation.readings);

          setInstallationConsumptionData(APIInstallationResponse);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("InstallationConsumptionHistory → fetchData → error", error);
      }

      setLoading(false);
    }
  }

  const memoDataToDisplay = React.useMemo(() => {
    if (installationConsumptionData?.readings) {
      const uom = installationConsumptionData.product_uom;
      setReportData([]);
      let dataSlot;
      let dailyData;

      if (useDataInSlot) {
        dataSlot =
          orderBy(
            installationConsumptionData.readings,
            function (o) {
              return o?.date ? new Date(o?.date).getTime() : 0;
            },
            "desc"
          ) || [];
        return dataSlot?.map((d, idx) => {
          mapReportData(
            formatDateTime(d.date),
            formatValue(useNormalizedData ? d.level : d.raw_level, 2) as number,
            formatValue((useNormalizedData ? d.level : d.raw_level) / HOURS_BETWEEN_SLOTS, 2) as number
          );
          return [
            <strong key={`date_${idx}`}>{keepDateFromTimezonePtBr(d.date)}</strong>,
            `${formatValue(useNormalizedData ? d.level : d.raw_level, 2)}${uom ? ` ${uom}` : ""}`,
            `${formatValue((useNormalizedData ? d.level : d.raw_level) / HOURS_BETWEEN_SLOTS, 2)} ${
              uom ? `${uom}/h` : ""
            }`,
          ];
        });
      } else {
        dailyData =
          orderBy(
            dailyConsumptionData,
            function (o) {
              return o?.date ? new Date(o?.date).getTime() : 0;
            },
            "desc"
          ) || [];
        return dailyData?.map((d: IConsumptionReading, idx) => {
          mapReportData(
            formatDateTime(d.date),
            formatValue(useNormalizedData ? d.level : d.raw_level, 2) as number,
            formatValue((useNormalizedData ? d.level : d.raw_level) / HOURS_IN_THE_DAY, 2) as number
          );
          return [
            <strong key={`date_${idx}`}>{keepDateFromTimezonePtBr(d.date)}</strong>,
            `${formatValue(useNormalizedData ? d.level : d.raw_level, 2)}${uom ? ` ${uom}` : ""}`,
            `${formatValue((useNormalizedData ? d.level : d.raw_level) / 24, 2)} ${
              uom ? `${uom}/h` : ""
            }`,
          ];
        });
      }
    }
    return [];
  }, [dailyConsumptionData, installationConsumptionData, useDataInSlot, useNormalizedData]);


  React.useEffect(() => {
    const doFetch = async () => await fetchData();
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installation?.installation_id, filterInterval]);

  const headers = ["Data", "Consumo", "Média de Consumo por Hora"];

  const headersFileExport = [
    "Data",
    "Consumo (" + installation?.product_uom + ")",
    "Média de Consumo por Hora (" + installation?.product_uom + ")",
  ];

  if (exportExcel) {
    // Executa aqui
    if (reportData) {
      const exportFile: IExportExcel = {
        headers: headersFileExport,
        data: reportData,
        fileName: installation?.product_code
          ? "Historico_Consumo_" + installation.product_code + " #" + installation.installation_id
          : "Historico_Consumo",
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fetchFile(exportFile);
    }

    setExportExcel(false);
  }

  if (exportPdf) {
    const exportFile: IHtml = {
      htmlString: renderToString(
        <ReportGrid headerReport="Histórico de Consumo">
          <ReportSimpleTable header={headers} rows={memoDataToDisplay} />
        </ReportGrid>
      ),
      fileName: installation?.product_code
        ? "Histórico_Consumo_" + installation.product_code + " #" + installation.installation_id
        : "Histórico_Consumo",
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchPdf(exportFile);

    setExportPdf(false);
  }

  return (
    <div className={classes.root}>
      <SimpleTable header={headers} rows={memoDataToDisplay} loading={loading} />
    </div>
  );
}

export default InstallationConsumptionHistory;
